import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import SeoHelmet from "./SeoHelmet";
import MonoButton from "../kits/DesignKit/MonoButton";
import { PaperGradientBG } from "../kits";
import TerminalWindow from "./terminal/TerminalWindow";
import TerminalLine from "./terminal/TerminalLine";
import TerminalScreenshot from "./TerminalScreenshot";
import useTypewriter from "../hooks/useTypewriter";
import useDesktop from "../hooks/useDesktop";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import useAnimationOverride from "../hooks/useAnimationOverride";

// ─── Data ──────────────────────────────────────────────

const PLATFORMS = [
  { name: "facebook", format: "long-form posts", desc: "Long-Form Posts mit optimalem Format" },
  { name: "x-twitter", format: "threads", desc: "Thread-Support mit Zeichen-Optimierung" },
  { name: "linkedin", format: "business-ton", desc: "Business-Ton & klare Struktur" },
  { name: "instagram", format: "captions+hashtags", desc: "Captions & Hashtags für Reichweite" },
  { name: "reddit", format: "tldr+subreddit", desc: "TL;DR & Subreddit-Vorschläge" },
  { name: "threads", format: "conversation", desc: "Conversation-Ready Posts" },
  { name: "youtube", format: "titel+beschreibung", desc: "Titel, Beschreibung & Shorts-Teaser" },
  { name: "medium", format: "publizieren", desc: "Publizieren mit passender Struktur" },
  { name: "dev-to", format: "developer-tags", desc: "Developer-Optimierung inklusive Tags" },
];

const DOC_FEATURES = [
  { title: "readme-gen", lines: ["readme & docs aus projektdaten", "automatisch generiert"] },
  { title: "context-wizard", lines: ["erfasst grundlagen deines projekts", "stack, ziele, architektur"] },
  { title: "project-scan", lines: ["erkennt struktur automatisch", "dependencies, scripts, config"] },
  { title: "data-room", lines: ["investor-ready dokumentation", "strukturiert und exportierbar"] },
  { title: "pro-features", lines: ["hard-scan fuer tiefe analyse", "versionierung & history"] },
  { title: "shipping-ready", lines: ["changelog, api-docs, guides", "sofort einsatzbereit"] },
];

const WORKFLOW_STEPS = [
  { num: "01", title: "schreiben oder import", desc: "nutze zeneditor oder importiere bestehende blogposts." },
  { num: "02", title: "plattformen auswaehlen", desc: "waehle die kanaele fuer deine inhalte in sekunden." },
  { num: "03", title: "posts generieren", desc: "ki erstellt plattform-optimierte varianten automatisch." },
  { num: "04", title: "feinschliff & teilen", desc: "passe details an, publiziere direkt oder automatisch." },
];

const PRICING_FREE = {
  included: [
    "reines schreiben (web oder desktop)",
    "lokale ki mit ollama (optional)",
    "lokale datenspeicherung",
    "100% offline",
    "unbegrenzte nutzung",
  ],
  excluded: ["kein publisher", "kein doc studio"],
};

const PRICING_PRO = {
  included: [
    "alles aus free",
    "datei projekt management",
    "posting planner und schedule",
    "api key integration",
    "analytics & insights",
    "custom templates",
    "publisher 9+ plattformen",
    "versionierung & history",
    "doc studio mit hardscan",
    "ki anbindung 5+",
  ],
};

const DOC_PRICING_MONTHLY = {
  included: [
    "doc studio freischalten",
    "project context wizard",
    "project scan + struktur",
    "readme / docs / data room",
    "flexibel kuendbar",
  ],
};

const DOC_PRICING_LIFETIME = {
  included: [
    "alles aus monatlich",
    "lifetime zugang",
    "alle zukuenftigen updates",
    "priority support",
    "hard-scan pro features",
  ],
};

const FAQS = [
  { q: "welche plattformen unterstuetzt zenpost studio?", a: "aktuell optimiert fuer linkedin, x, instagram, reddit, threads und facebook. weitere plattformen folgen." },
  { q: "kann ich die generierten posts anpassen?", a: "ja. du kannst jede version bearbeiten, kuerzen oder erweitern, bevor du sie veroeffentlichst." },
  { q: "wie funktioniert die lokale ki?", a: "zenpost studio nutzt ollama, damit deine texte lokal auf deinem geraet verarbeitet werden. keine cloud, keine api-kosten." },
  { q: "ist zenpost studio wirklich kostenlos?", a: "ja. zenpost studio bleibt dauerhaft kostenlos und ist komplett offline nutzbar." },
  { q: "kann ich mehrere artikel auf einmal umwandeln?", a: "mit zenpost studio pro kannst du mehrere inhalte parallel vorbereiten, verwalten und auf 9+ plattformen transformieren." },
];

const APP_URL = "https://zenpost.denisbitter.de";

const trackCTA = (ctaName) => {
  if (window.gtag) {
    window.gtag("event", "cta_click", { event_category: "ZenPostStudio", event_label: ctaName });
  }
};

// ─── Hero Terminal (Interactive) ───────────────────────

const HERO_BOOT = [
  { type: "prompt", text: "$ zenpost init" },
  { type: "status", text: "> ZenPost Studio v1.0.4 (Beta)" },
  { type: "response", text: "> schreibe einmal. transformiere 9+." },
  { type: "prompt", text: "$ zenpost --stats" },
  { type: "response", text: "> plattformen   9+" },
  { type: "response", text: "> ki-modelle    lokal" },
  { type: "response", text: "> offline       ja" },
  { type: "response", text: "> zugang: offen (inkl. DocStudio)" },
  { type: "prompt", text: "$ zenpost --info" },
  { type: "response", text: "> schreibe einmal und erstelle daraus optimierte" },
  { type: "response", text: "> posts fuer 9 plattformen. inklusive doc studio" },
  { type: "response", text: "> fuer readme, changelog und projekt-dokumentation." },
  { type: "status", text: "> ready." },
];

const HERO_COMMANDS = {
  help: [
    { type: "response", text: "> verfuegbare befehle:" },
    { type: "response", text: ">   help       – diese hilfe" },
    { type: "response", text: ">   demo       – app oeffnen" },
    { type: "response", text: ">   pricing    – zu den preisen" },
    { type: "response", text: ">   features   – features anzeigen" },
    { type: "response", text: ">   platforms  – plattformen anzeigen" },
    { type: "response", text: ">   clear      – terminal leeren" },
  ],
  platforms: [
    { type: "response", text: "> 9 plattformen konfiguriert:" },
    ...PLATFORMS.map((p) => ({ type: "response", text: `>   ${p.name}` })),
  ],
};

const HERO_SHORTCUTS = [
  { key: "d", label: "Demo", id: "demo" },
  { key: "p", label: "Preise", id: "pricing" },
  { key: "f", label: "Features", id: "features" },
];

function HeroTerminal({ onDone }) {
  const isDesktop = useDesktop();
  const prefersReduced = usePrefersReducedMotion();
  const animOverride = useAnimationOverride();
  const shouldAnimate = animOverride ?? (isDesktop && !prefersReduced);
  const heroRef = useRef(null);
  const [inView, setInView] = useState(true);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [interactiveLines, setInteractiveLines] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const { visibleLines: bootLinesTyped, isTyping: isTypingTyped, done: doneTyped } = useTypewriter(
    HERO_BOOT,
    600,
    !inView || !shouldAnimate
  );
  const bootLines = useMemo(
    () => (shouldAnimate ? bootLinesTyped : HERO_BOOT.map((l) => ({ ...l, displayText: l.text }))),
    [shouldAnimate, bootLinesTyped]
  );
  const isTyping = shouldAnimate ? isTypingTyped : false;
  const done = shouldAnimate ? doneTyped : true;
  const allLines = useMemo(() => [...bootLines, ...interactiveLines], [bootLines, interactiveLines]);

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [allLines]);

  useEffect(() => {
    if (done && inputRef.current) inputRef.current.focus();
    if (done) onDone?.();
  }, [done, onDone]);

  useEffect(() => {
    if (!shouldAnimate) return;
    if (!heroRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.2 }
    );
    obs.observe(heroRef.current);
    return () => obs.disconnect();
  }, [shouldAnimate]);

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const handleCommand = useCallback((cmd) => {
    const trimmed = cmd.trim().toLowerCase();
    const promptLine = { type: "prompt", displayText: `$ ${cmd.trim()}` };
    if (!trimmed) return;

    setCommandHistory((prev) => [...prev, cmd.trim()]);
    setHistoryIndex(-1);

    if (trimmed === "clear") { setInteractiveLines([]); return; }

    if (trimmed === "demo") {
      setInteractiveLines((prev) => [...prev, promptLine, { type: "status", displayText: "> oeffne zenpost studio..." }]);
      trackCTA("Hero_Demo");
      setTimeout(() => window.open(APP_URL, "_blank", "noopener,noreferrer"), 600);
      return;
    }

    if (trimmed === "pricing") {
      setInteractiveLines((prev) => [...prev, promptLine, { type: "status", displayText: "> scrolle zu preise..." }]);
      setTimeout(() => scrollTo("pricing"), 400);
      return;
    }

    if (trimmed === "features") {
      setInteractiveLines((prev) => [...prev, promptLine, { type: "status", displayText: "> scrolle zu features..." }]);
      setTimeout(() => scrollTo("features"), 400);
      return;
    }

    if (HERO_COMMANDS[trimmed]) {
      setInteractiveLines((prev) => [...prev, promptLine, ...HERO_COMMANDS[trimmed].map((l) => ({ ...l, displayText: l.text }))]);
      return;
    }

    setInteractiveLines((prev) => [
      ...prev, promptLine,
      { type: "error", displayText: `> command not found: ${cmd.trim()}` },
      { type: "response", displayText: `> tipp "help" oder drueck [d] [p] [f]` },
    ]);
  }, [scrollTo]);

  const handleShortcut = useCallback((s) => {
    if (s.id === "demo") { handleCommand("demo"); return; }
    const promptLine = { type: "prompt", displayText: `$ ${s.id}` };
    setInteractiveLines((prev) => [...prev, promptLine, { type: "status", displayText: `> scrolle zu ${s.label.toLowerCase()}...` }]);
    setTimeout(() => scrollTo(s.id), 400);
  }, [handleCommand, scrollTo]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { handleCommand(inputValue); setInputValue(""); return; }
    if (!inputValue && done) {
      const s = HERO_SHORTCUTS.find((sc) => sc.key === e.key.toLowerCase());
      if (s) { e.preventDefault(); handleShortcut(s); return; }
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!commandHistory.length) return;
      const idx = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(idx); setInputValue(commandHistory[idx]); return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex === -1) return;
      const idx = historyIndex + 1;
      if (idx >= commandHistory.length) { setHistoryIndex(-1); setInputValue(""); }
      else { setHistoryIndex(idx); setInputValue(commandHistory[idx]); }
    }
  };

  const focusInput = () => { if (done && inputRef.current) inputRef.current.focus(); };

  return (
    <div ref={heroRef}>
      <TerminalWindow
        title="zenpost-studio://launch"
        maxWidth="max-w-4xl"
        shortcuts={done ? HERO_SHORTCUTS.map((s) => ({ ...s, onClick: () => handleShortcut(s) })) : undefined}
      >
        <div ref={containerRef} className="max-h-[60vh] overflow-y-auto cursor-text" onClick={focusInput}>
          {allLines.map((line, i) => {
            const showCursor = isTyping && i === bootLines.length - 1 && line.type !== "prompt";
            return <TerminalLine key={i} type={line.type} text={line.displayText || line.text} showCursor={showCursor} />;
          })}
          {done && (
            <div className="mt-3 flex items-center text-sm sm:text-sm">
              <span className="text-[#AC8E66]">$</span>
              {inputValue
                ? <><span className="ml-1 text-[#EFEBDC] whitespace-pre">{inputValue}</span><span className="inline-block w-[2px] h-[1.1em] bg-[#AC8E66] animate-cursor-blink flex-none" /></>
                : <><span className="inline-block w-[2px] h-[1.1em] bg-[#AC8E66] ml-1 animate-cursor-blink flex-none" /><span className="text-[#EFEBDC]/40 text-[10px] whitespace-pre ml-1">tippe help · demo · pricing</span></>
              }
              <input ref={inputRef} type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={handleKeyDown} className="sr-only" spellCheck={false} autoComplete="off" />
            </div>
          )}
        </div>
      </TerminalWindow>
    </div>
  );
}

// ─── FAQ Terminal (Interactive) ────────────────────────

function FAQTerminal() {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [lines, setLines] = useState([
    { type: "prompt", displayText: "$ zenpost faq --list" },
    { type: "response", displayText: `> ${FAQS.length} fragen verfuegbar. tippe 1-${FAQS.length} oder "all".` },
    ...FAQS.map((f, i) => ({ type: "response", displayText: `> ${i + 1}. ${f.q}` })),
  ]);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [lines]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const handleCommand = useCallback((cmd) => {
    const trimmed = cmd.trim().toLowerCase();
    const promptLine = { type: "prompt", displayText: `$ ${cmd.trim()}` };
    if (!trimmed) return;

    if (trimmed === "clear") { setLines([]); return; }

    if (trimmed === "all") {
      const allAnswers = FAQS.flatMap((f, i) => [
        { type: "response", displayText: `> ${i + 1}. ${f.q}` },
        { type: "status", displayText: `> ${f.a}` },
      ]);
      setLines((prev) => [...prev, promptLine, ...allAnswers]);
      return;
    }

    const num = parseInt(trimmed, 10);
    if (num >= 1 && num <= FAQS.length) {
      const faq = FAQS[num - 1];
      setLines((prev) => [
        ...prev, promptLine,
        { type: "response", displayText: `> ${faq.q}` },
        { type: "status", displayText: `> ${faq.a}` },
      ]);
      return;
    }

    setLines((prev) => [
      ...prev, promptLine,
      { type: "error", displayText: `> ungueltig. tippe 1-${FAQS.length} oder "all".` },
    ]);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { handleCommand(inputValue); setInputValue(""); return; }
    if (!inputValue) {
      const s = FAQS.find((_, i) => String(i + 1) === e.key);
      if (s) { e.preventDefault(); handleCommand(e.key); return; }
    }
  };

  const faqShortcuts = FAQS.map((_, i) => ({
    key: String(i + 1),
    label: `Frage ${i + 1}`,
    onClick: () => handleCommand(String(i + 1)),
  }));

  const focusInput = () => { if (inputRef.current) inputRef.current.focus(); };

  return (
    <TerminalWindow title="zenpost://faq" maxWidth="max-w-3xl" shortcuts={faqShortcuts}>
      <div ref={containerRef} className="max-h-[50vh] overflow-y-auto cursor-text" onClick={focusInput}>
        {lines.map((line, i) => (
          <TerminalLine key={i} type={line.type} text={line.displayText} />
        ))}
        <div className="mt-3 flex items-center text-sm sm:text-sm">
          <span className="text-[#AC8E66]">$</span>
          {inputValue
            ? <><span className="ml-1 text-[#EFEBDC] whitespace-pre">{inputValue}</span><span className="inline-block w-[2px] h-[1.1em] bg-[#AC8E66] animate-cursor-blink flex-none" /></>
            : <><span className="inline-block w-[2px] h-[1.1em] bg-[#AC8E66] ml-1 animate-cursor-blink flex-none" /><span className="text-[#EFEBDC]/40 text-[10px] whitespace-pre ml-1">tippe 1-5 · all</span></>
          }
          <input ref={inputRef} type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={handleKeyDown} className="sr-only" spellCheck={false} autoComplete="off" />
        </div>
      </div>
    </TerminalWindow>
  );
}

// ─── Pricing Card ──────────────────────────────────────

function PricingTerminal({ title, price, period, included, excluded, ctaText, onCta, popular }) {
  return (
    <TerminalWindow
      title={title}
      maxWidth="w-full"
      popular={popular}
      contentClassName="flex flex-col min-h-[320px]"
    >
      <div className="flex-1">
        <TerminalLine type="response" text="> pricing: disabled" />
        <TerminalLine type="response" text="> reason: beta phase" />
        <div className="mt-2 mb-1 border-t border-[#AC8E66]/20" />
        {included.map((f, i) => (
          <p key={i} className="text-sm text-green-400 mt-1">
            <span className="text-green-400/70">[x]</span> {f}
          </p>
        ))}
        {excluded?.map((f, i) => (
          <p key={`ex-${i}`} className="text-sm text-[#EFEBDC]/30 mt-1">
            <span className="text-[#EFEBDC]/20">[ ]</span> {f}
          </p>
        ))}
      </div>
      {onCta && (
        <div className="mt-4">
          <MonoButton onClick={onCta} className="text-xs w-full">{ctaText}</MonoButton>
        </div>
      )}
    </TerminalWindow>
  );
}

// ─── CTA Terminal with InView Typewriter ───────────────

function CTATerminal({ onDone }) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setStarted(true); observer.disconnect(); }
    }, { threshold: 0.5 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const ctaLines = started ? [
    { type: "prompt", text: "$ zenpost summary" },
    { type: "response", text: "> weniger copy-paste. mehr zeit fuer inhalte." },
    { type: "status", text: "> ready to launch." },
  ] : [];

  const { visibleLines, isTyping, done } = useTypewriter(ctaLines, 300);

  useEffect(() => {
    if (done) onDone?.();
  }, [done, onDone]);

  return (
    <div ref={ref}>
      <TerminalWindow title="zenpost://launch" maxWidth="max-w-4xl">
        <div className="min-h-[80px]">
          {visibleLines.map((line, i) => {
            const showCursor = isTyping && i === visibleLines.length - 1 && line.type !== "prompt";
            return <TerminalLine key={i} type={line.type} text={line.displayText} showCursor={showCursor} />;
          })}
          {done && (
            <div className="mt-4">
              <MonoButton
                onClick={() => { trackCTA("Final_CTA"); window.open(APP_URL, "_blank", "noopener,noreferrer"); }}
                className="text-xs w-full"
              >
                $ zenpost --start
              </MonoButton>
            </div>
          )}
        </div>
      </TerminalWindow>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────

const ZenPostStudioLandingPage = () => {
  const isDesktop = useDesktop();
  const prefersReduced = usePrefersReducedMotion();
  const animOverride = useAnimationOverride();
  const shouldAnimate = animOverride ?? (isDesktop && !prefersReduced);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [finalDone, setFinalDone] = useState(false);
  const [heroDone, setHeroDone] = useState(!shouldAnimate);

  useEffect(() => {
    if (!shouldAnimate) setHeroDone(true);
  }, [shouldAnimate]);

  useEffect(() => {
    if (!finalDone) return;
    const t = setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 300);
    return () => clearTimeout(t);
  }, [finalDone]);

  const openApp = () => {
    trackCTA("Open_App");
    window.open(APP_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <SeoHelmet
        title="ZenPost Studio – Schreiben, Dokumentieren, Publizieren"
        description="ZenPost Studio: Schreibe einmal, erstelle Posts für verschiedene Plattformen. Inklusive Doc Studio für Projekt-Dokumentation. Kostenlose Desktop-App mit lokaler KI."
        keywords="zenpost studio, schreiben, dokumentation, lokale ki, ollama, markdown editor, doc studio"
        ogImage="/images/zenpoststudio-og.jpg"
      />

      <PaperGradientBG variant="zenBlack">
        <div className="min-h-screen font-mono text-[#EFEBDC]">

          {/* ── Section 1: Hero Terminal ──────────────────── */}
          <section className="py-16 sm:py-24 px-4">
            <HeroTerminal onDone={() => setHeroDone(true)} />
          </section>

          <main
            className={`${heroDone ? "opacity-100" : "opacity-0 pointer-events-none"} transition-opacity duration-900 ease-out`}
            aria-hidden={!heroDone}
          >
          {/* ── Screenshot: Welcome ─────────────────────── */}
          <section className="py-8 sm:py-12">
            <TerminalScreenshot
              src="https://denisbitter.de/images/zenPost/zenpost_prevgit.webp"
              alt="ZenPost Studio Content AI"
              caption="preview://willkommen"
            />
          </section>

          {/* ── Section 2: Doc Studio Panel ──────────────── */}
          <section className="py-12 sm:py-16 px-4">
            <TerminalWindow title="doc-studio://overview" maxWidth="max-w-4xl">
              <TerminalLine type="prompt" text="$ docstudio analyze --project zenpost" />
              <TerminalLine type="response" text="> [scan] project context wizard ... ok" />
              <TerminalLine type="response" text="> [scan] automatische projektstruktur ... ok" />
              <TerminalLine type="response" text="> [scan] dependencies & scripts ... ok" />
              <div className="mt-3" />
              <TerminalLine type="response" text="> ergebnis:" />
              <TerminalLine type="response" text=">   -- project context wizard erfasst grundlagen" />
              <TerminalLine type="response" text=">   -- automatische projektstruktur-erkennung" />
              <TerminalLine type="response" text=">   -- strukturierte dokumentation mit klaren todos" />
              <div className="mt-3" />
              <TerminalLine type="status" text="> docs ready. 4 templates verfuegbar." />
            </TerminalWindow>
          </section>

          {/* ── Screenshot: Doc Studio Templates ────────── */}
          <section className="py-8 sm:py-12">
            <TerminalScreenshot
              src="/images/zenpoststudio/docstudio.png"
              alt="Doc Studio Templates"
              caption="preview://doc-studio"
            />
          </section>

          {/* ── Section 3: Doc Studio Features Grid ──────── */}
          <section id="features" className="py-12 sm:py-16 px-4">
            <div className="max-w-5xl mx-auto">
              <div className="mb-8 px-4 sm:px-6">
                <TerminalLine type="prompt" text="$ docstudio features --list" />
                <TerminalLine type="response" text="> 6 features verfuegbar:" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {DOC_FEATURES.map((f) => (
                  <TerminalWindow key={f.title} title={f.title} maxWidth="w-full" contentClassName="py-3 px-4 min-h-[80px]">
                    {f.lines.map((line, i) => (
                      <TerminalLine key={i} type="response" text={`> ${line}`} />
                    ))}
                  </TerminalWindow>
                ))}
              </div>
            </div>
          </section>

             {/* ── Screenshot: Doc Studio Feature ────────── */}
          <section className="py-8 sm:py-12">
            <TerminalScreenshot
              src="/images/zenpoststudio/docstudio-feature.png"
              alt="Doc Studio Templates"
              caption="preview://doc-studio features doc templates"
            />
          </section>

          {/* ── Section 4: Platforms Table ────────────────── */}
          <section id="platforms" className="py-12 sm:py-16 px-4">
            <TerminalWindow title="content-ai://platforms" maxWidth="max-w-4xl">
              <TerminalLine type="prompt" text="$ zenpost platforms --list" />
              <div className="mt-2" />
              {/* Desktop table */}
              <div className="hidden sm:block">
                <p className="text-xs text-[#EFEBDC]/40 mt-1 font-mono">
                  {"  NAME           FORMAT               STATUS"}
                </p>
                <p className="text-xs text-[#EFEBDC]/20 mt-0.5 font-mono">
                  {"  ─────────      ─────────────        ──────"}
                </p>
                {PLATFORMS.map((p) => (
                  <p key={p.name} className="text-sm text-[#EFEBDC]/80 mt-0.5 font-mono">
                    {"  "}
                    <span>{p.name.padEnd(15)}</span>
                    <span className="text-[#EFEBDC]/60">{p.format.padEnd(21)}</span>
                    <span className="text-green-400">[aktiv]</span>
                  </p>
                ))}
              </div>
              {/* Mobile list */}
              <div className="sm:hidden">
                {PLATFORMS.map((p) => (
                  <p key={p.name} className="text-sm text-[#EFEBDC]/80 mt-1 font-mono">
                    <span className="text-[#AC8E66]/70">&gt;</span> {p.name} <span className="text-green-400 text-xs">[aktiv]</span>
                  </p>
                ))}
              </div>
              <div className="mt-3" />
              <TerminalLine type="status" text={`> ${PLATFORMS.length} plattformen konfiguriert. 0 fehler.`} />
            </TerminalWindow>
          </section>


                {/* ── Screenshot: Doc Studio Feature ────────── */}
          <section className="py-8 sm:py-12">
            <TerminalScreenshot
              src="/images/zenpoststudio/smapi.png"
              alt="Doc Studio Templates"
              caption="preview://Social Media Transformation"
            />
          </section>

          {/* ── Screenshot: Editor BLOCK──────────────────────── */}
          <section className="py-8 sm:py-12">
            <TerminalScreenshot
              src="/images/zenpoststudio/editorBlock.png"
              alt="ZenPost Studio Editor"
              caption="preview://block-editor content AI Studio"
            />
          </section>

             {/* ── Screenshot: Editor MD ──────────────────────── */}
          <section className="py-8 sm:py-12">
            <TerminalScreenshot
              src="/images/zenpoststudio/editorMD.png"
              alt="ZenPost Studio Editor"
              caption="preview://markdwon-editor content AI Studio"
            />
          </section>

          {/* ── Section 5: Features Panel ─────────────────── */}
          <section className="py-12 sm:py-16 px-4">
            <TerminalWindow title="content-ai://features" maxWidth="max-w-4xl">
              <TerminalLine type="prompt" text="$ zenpost features --show" />
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                {[
                  { tag: "transformation", desc: "wandle jeden artikel sofort in 9+ plattform posts um." },
                  { tag: "plattform-optimiert", desc: "automatische anpassung an ton, laenge & format." },
                  { tag: "lokale-dokumente", desc: "relevante ablage auf deinem desktop fuer maximale sicherheit." },
                  { tag: "data-room", desc: "erstelle echte dokumente fuer deine projekte." },
                ].map((f) => (
                  <div key={f.tag} className="border border-[#AC8E66]/10 rounded px-4 py-3 flex flex-col">
                    <p className="text-sm text-[#AC8E66] font-mono">[{f.tag}]</p>
                    <p className="text-sm text-[#EFEBDC]/70 mt-1 flex-1">{f.desc}</p>
                  </div>
                ))}
              </div>
            </TerminalWindow>
          </section>

          {/* ── Screenshot: Plattformen KI ─────────────── */}
          <section className="py-8 sm:py-12">
            <TerminalScreenshot
              src="/images/zenpoststudio/Zenpost_plattformenKI.png"
              alt="ZenPost Studio Plattformen KI"
              caption="preview://plattformen"
            />
          </section>

          {/* ── Gold separator ────────────────────────────── */}
          <div className="max-w-md mx-auto border-t border-[#AC8E66]/20" />

          {/* ── Section 6: Ollama Dual Panel ──────────────── */}
          <section className="py-12 sm:py-16 px-4">
            <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-4">
              <TerminalWindow title="ollama://local-ai" maxWidth="w-full">
                <TerminalLine type="prompt" text="$ ollama status" />
                <div className="mt-2" />
                <TerminalLine type="response" text="> engine:       lokal" />
                <TerminalLine type="response" text="> api-kosten:   0 EUR" />
                <TerminalLine type="response" text="> privatsphaere: 100%" />
                <TerminalLine type="response" text="> token-limit:  unbegrenzt" />
                <div className="mt-3" />
                <TerminalLine type="response" text="> installiere ollama einmal." />
                <TerminalLine type="response" text="> zenpost studio erkennt es automatisch." />
                <TerminalLine type="status" text="> modelle laufen auf deinem rechner." />
              </TerminalWindow>
              <TerminalWindow title="ollama://models" maxWidth="w-full">
                <TerminalLine type="prompt" text="$ ollama list" />
                <TerminalLine type="response" text="> verfuegbare modelle:" />
                <div className="mt-3 flex flex-wrap gap-2">
                  {["llama-2", "mistral", "codellama", "custom"].map((m) => (
                    <kbd key={m} className="inline-flex items-center justify-center px-2.5 py-1 rounded border border-[#AC8E66]/30 bg-[#AC8E66]/10 text-[#AC8E66] text-xs font-mono">
                      {m}
                    </kbd>
                  ))}
                </div>
                <div className="mt-4" />
                <TerminalLine type="response" text="> 100% offline. keine api-kosten." />
                <TerminalLine type="response" text="> volle privatsphaere." />
                <TerminalLine type="status" text="> unbegrenzte nutzung." />
              </TerminalWindow>
            </div>
          </section>

                {/* ── Screenshot: Ollama lokal Feature ────────── */}
          <section className="py-8 sm:py-12">
            <TerminalScreenshot
              src="/images/zenpoststudio/lokaleAI.png"
              alt="Doc Studio Templates"
              caption="preview://Ollama local KI"
            />
          </section>

                {/* ── Screenshot: AI Studio Feature ────────── */}
          <section className="py-8 sm:py-12">
            <TerminalScreenshot
              src="/images/zenpoststudio/aieinstellungen.png"
              alt="Doc Studio Templates"
              caption="preview://AI Setting"
            />
          </section>

          {/* ── Section 7: Workflow Pipeline ──────────────── */}
          <section id="workflow" className="py-12 sm:py-16 px-4">
            <div className="max-w-3xl mx-auto">
              <TerminalLine type="prompt" text="$ zenpost workflow --show" className="px-4 sm:px-6" />
              <div className="mt-6 space-y-6 pl-4 sm:pl-6 border-l border-[#AC8E66]/20 ml-6 sm:ml-8">
                {WORKFLOW_STEPS.map((step) => (
                  <div key={step.num} className="relative">
                    <div className="absolute -left-[calc(0.75rem+1px)] top-0 w-6 h-6 rounded-full bg-[#1a1a1a] border border-[#AC8E66]/30 flex items-center justify-center">
                      <span className="text-[10px] text-[#AC8E66] font-mono">{step.num}</span>
                    </div>
                    <div className="pl-6">
                      <p className="text-sm sm:text-sm text-[#EFEBDC] font-mono">{step.title}</p>
                      <p className="text-xs sm:text-sm text-[#EFEBDC]/50 mt-1">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <TerminalLine type="status" text="> workflow complete. exit code 0." className="mt-6 px-4 sm:px-6" />
            </div>
          </section>

          {/* ── Gold separator ────────────────────────────── */}
          <div className="max-w-md mx-auto border-t border-[#AC8E66]/20" />

          {/* ── Section 8: Pricing ZenPost ────────────────── */}
          <section id="pricing" className="py-12 sm:py-16 px-4">
            <div className="max-w-5xl mx-auto">
              <div className="mb-8 px-4 sm:px-6">
                <TerminalLine type="prompt" text="$ zenpost pricing --show" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <PricingTerminal
                  title="pricing://free"
                  price="Beta kostenlos"
                  included={PRICING_FREE.included}
                  excluded={PRICING_FREE.excluded}
                  ctaText="$ zenpost --start"
                  onCta={openApp}
                />
                <PricingTerminal
                  title="pricing://pro"
                  price="Beta kostenlos"
                  period=" / lifetime"
                  included={PRICING_PRO.included}
                  ctaText="$ zenpost pro --start"
                  onCta={openApp}
                  popular
                />
              </div>
            </div>
          </section>

          {/* ── Section 9: Pricing Doc Studio ─────────────── */}
          <section className="py-12 sm:py-16 px-4">
            <div className="max-w-5xl mx-auto">
              <div className="mb-8 px-4 sm:px-6">
                <TerminalLine type="prompt" text="$ docstudio pricing --show" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <PricingTerminal
                  title="docstudio://monthly"
                  price="Beta kostenlos"
                  period=" / monat"
                  included={DOC_PRICING_MONTHLY.included}
                  ctaText="$ docstudio --subscribe"
                  onCta={() => { trackCTA("DocStudio_Monthly"); window.open(`${APP_URL}?upgrade=docstudio&plan=monthly`, "_blank", "noopener,noreferrer"); }}
                />
                <PricingTerminal
                  title="docstudio://lifetime"
                  price="Beta kostenlos"
                  period=" / einmalig"
                  included={DOC_PRICING_LIFETIME.included}
                  ctaText="$ docstudio --lifetime"
                  onCta={() => { trackCTA("DocStudio_Lifetime"); window.open(`${APP_URL}?upgrade=docstudio&plan=lifetime`, "_blank", "noopener,noreferrer"); }}
                  popular
                />
              </div>
            </div>
          </section>

          {/* ── Gold separator ────────────────────────────── */}
          <div className="max-w-md mx-auto border-t border-[#AC8E66]/20" />

          {/* ── Section 10: FAQ Terminal ──────────────────── */}
          <section className="py-12 sm:py-16 px-4">
            <FAQTerminal />
          </section>

          {/* ── Section 11: Final CTA ────────────────────── */}
          <section className="py-16 sm:py-24 px-4">
            <CTATerminal onDone={() => setFinalDone(true)} />
          </section>

          </main>
        </div>
      </PaperGradientBG>
    </>
  );
};

export default ZenPostStudioLandingPage;
